import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';

// Lazy load Swiper components
const Swiper = lazy(() => import('swiper/react').then(module => ({ default: module.Swiper })));
const SwiperSlide = lazy(() => import('swiper/react').then(module => ({ default: module.SwiperSlide })));

// Import Swiper modules directly
let Autoplay: any = null;
let SwiperModules: any = null;

export interface TickerSlide {
	date: string;
	title: string;
	link: string;
}

interface NavigationTickerProps {
	slides: TickerSlide[];
	className?: string;
	isVisible?: boolean;
	isMobile?: boolean;
	onMouseLeave?: () => void;
}

// Static ticker fallback component
const StaticTicker: React.FC<{ slides: TickerSlide[]; className?: string }> = ({ slides, className = '' }) => (
	<div className={`swiper js-navigation-ticker swiper-initialized swiper-vertical swiper-backface-hidden ${className}`}>
		<div className="swiper-wrapper">
			{slides.map((slide, i) => (
				<div
					className={`swiper-slide${i === 0 ? ' swiper-slide-active' : ''}`}
					style={{ height: 107 }}
					data-swiper-slide-index={i}
					key={slide.title}
				>
					<div className="c-ticker-card">
						<span className="c-ticker-card__date u-uppercase">
							{slide.date}
						</span>
						<p className="c-ticker-card__title">
							<a
								href={slide.link}
								target="_blank"
								className="c-ticker-card__link"
								rel="noopener noreferrer"
							>
								{slide.title}
							</a>
						</p>
					</div>
				</div>
			))}
		</div>
	</div>
);

// Lazy loaded ticker component with Swiper
const LazyTickerComponent: React.FC<{ slides: TickerSlide[]; className?: string; isMobile?: boolean }> = ({ slides, className = '', isMobile = false }) => {
	const [isLoaded, setIsLoaded] = useState(false);
	const swiperRef = useRef<any>(null);

	useEffect(() => {
		// Load Swiper CSS and modules when component mounts
		const loadSwiper = async () => {
			try {
				// Load CSS first
				await import('swiper/css' as any);
				
				// Load modules
				const modules = await import('swiper/modules');
				SwiperModules = modules;
				Autoplay = modules.Autoplay;

			} catch {
			}
			setIsLoaded(true);
		};
		loadSwiper();
	}, []);

	if (!isLoaded) {
		return <StaticTicker slides={slides} className={className} />;
	}

	return (
		<Suspense fallback={<StaticTicker slides={slides} className={className} />}>
			<Swiper
				onSwiper={(swiper) => {
					swiperRef.current = swiper;
					console.log('Swiper instance created:', {
						isMobile,
						autoplay: swiper.autoplay,
						slidesPerView: swiper.params.slidesPerView,
						modules: swiper.modules
					});
				}}
				direction="vertical"
				loop={true}
				autoHeight={true}
				slidesPerView={1}
				spaceBetween={0}
				modules={SwiperModules ? [SwiperModules.Autoplay] : []}
				autoplay={SwiperModules ? {
					delay: 1000,
					pauseOnMouseEnter: false,
					disableOnInteraction: false,
					stopOnLastSlide: false
				} : false}
				className={`swiper js-navigation-ticker ${className} ${isMobile ? 'mobile-ticker' : ''}`}
				style={{ 
					height: '107px',
					overflow: 'hidden'
				}}
				// Force mobile-specific settings
				{...(isMobile && {
					slidesPerView: 1,
					spaceBetween: 0,
					allowTouchMove: false,
					simulateTouch: false
				})}
			>
				{slides.map((slide, idx) => (
					<SwiperSlide 
						key={idx} 
						style={{ 
							height: '107px',
							width: '100% !important',
							flexShrink: 0
						}}
					>
						<div className="c-ticker-card">
							<span className="c-ticker-card__date u-uppercase">
								{slide.date}
							</span>
							<p className="c-ticker-card__title">
								<a
									href={slide.link}
									target="_blank"
									rel="noopener noreferrer"
									className="c-ticker-card__link"
								>
									{slide.title}
								</a>
							</p>
						</div>
					</SwiperSlide>
				))}
			</Swiper>
		</Suspense>
	);
};

const NavigationTicker: React.FC<NavigationTickerProps> = ({ slides, className = '', isVisible = false, isMobile = false, onMouseLeave }) => {
	// Only render ticker when visible to save resources
	if (!isVisible) {
		return null;
	}

	return (
		<div 
			className={`c-navigation__ticker js-navigation-ticker-wrapper${isVisible ? ' is-open' : ''}`}
			onMouseLeave={onMouseLeave}
		>
			<LazyTickerComponent slides={slides} className={className} isMobile={isMobile} />
		</div>
	);
};

export default NavigationTicker; 